import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Band, BandMember } from '../types/database';
import { useAuth } from './useAuth';
import { slugify } from '../lib/utils';

export function useMyBands() {
  const { user } = useAuth();
  const [bands, setBands] = useState<Band[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBands = useCallback(async () => {
    if (!user) {
      setBands([]);
      setLoading(false);
      return;
    }

    const { data } = await supabase
      .from('band_members')
      .select('band_id, bands(*)')
      .eq('user_id', user.id)
      .eq('is_accepted', true);

    if (data) {
      setBands(data.map((d) => (d as unknown as { bands: Band }).bands).filter(Boolean));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchBands();
  }, [fetchBands]);

  return { bands, loading, refetch: fetchBands };
}

export function useBand(bandId: string | undefined) {
  const [band, setBand] = useState<Band | null>(null);
  const [members, setMembers] = useState<BandMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchBand = useCallback(async () => {
    if (!bandId) return;

    const { data: bandData } = await supabase
      .from('bands')
      .select('*')
      .eq('id', bandId)
      .single();

    if (bandData) setBand(bandData);

    const { data: memberData } = await supabase
      .from('band_members')
      .select('*, profile:profiles(*)')
      .eq('band_id', bandId);

    if (memberData) {
      setMembers(memberData as unknown as BandMember[]);
      if (user) {
        const currentMember = memberData.find((m) => m.user_id === user.id);
        setUserRole(currentMember?.role ?? null);
      }
    }

    setLoading(false);
  }, [bandId, user]);

  useEffect(() => {
    fetchBand();
  }, [fetchBand]);

  return { band, members, loading, userRole, refetch: fetchBand };
}

export function useBandBySlug(slug: string | undefined) {
  const [band, setBand] = useState<Band | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    async function fetch() {
      const { data } = await supabase
        .from('bands')
        .select('*')
        .eq('slug', slug)
        .eq('is_public', true)
        .single();

      setBand(data);
      setLoading(false);
    }
    fetch();
  }, [slug]);

  return { band, loading };
}

export async function createBand(
  name: string,
  location: string,
  country: string,
  description: string,
  genres: string[],
  userId: string
): Promise<{ band: Band | null; error: string | null }> {
  const slug = slugify(name);

  // Check slug uniqueness
  const { data: existing } = await supabase
    .from('bands')
    .select('id')
    .eq('slug', slug)
    .single();

  const finalSlug = existing ? `${slug}-${Date.now().toString(36)}` : slug;

  const { data: band, error } = await supabase
    .from('bands')
    .insert({
      name,
      slug: finalSlug,
      description,
      genres,
      location_name: location,
      location_country: country,
      created_by: userId,
    })
    .select()
    .single();

  if (error) return { band: null, error: error.message };

  // Add creator as owner
  await supabase.from('band_members').insert({
    band_id: band.id,
    user_id: userId,
    email: (await supabase.auth.getUser()).data.user?.email ?? '',
    role: 'owner',
    is_accepted: true,
    accepted_at: new Date().toISOString(),
  });

  return { band, error: null };
}

export async function updateMemberInstruments(
  memberId: string,
  instruments: string[]
): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('band_members')
    .update({ instruments })
    .eq('id', memberId);

  return { error: error?.message ?? null };
}

export async function inviteMember(
  bandId: string,
  email: string,
  role: 'admin' | 'member',
  invitedBy: string
): Promise<{ error: string | null }> {
  // Check if user already exists
  const { data: existingProfile } = await supabase
    .from('profiles')
    .select('id')
    .eq('email', email)
    .single();

  if (existingProfile) {
    // User exists - add them directly
    const { error } = await supabase.from('band_members').insert({
      band_id: bandId,
      user_id: existingProfile.id,
      email,
      role,
      is_accepted: true,
      accepted_at: new Date().toISOString(),
    });
    if (error) return { error: error.message };
  } else {
    // User doesn't exist - create pending member and invitation
    const { error: memberError } = await supabase.from('band_members').insert({
      band_id: bandId,
      email,
      role,
      is_accepted: false,
    });
    if (memberError) return { error: memberError.message };

    const { error: inviteError } = await supabase.from('band_invitations').insert({
      band_id: bandId,
      email,
      role,
      invited_by: invitedBy,
    });
    if (inviteError) return { error: inviteError.message };
  }

  return { error: null };
}
